import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Plus, Search, LayoutGrid, LayoutList, Building2, SearchX,
} from 'lucide-react';
import authAxios from '../utils/authAxios';
import { CustomerListRow, CustomerCard } from '../components/CustomerListItems';
import CustomerDetailPanel from '../components/CustomerDetailPanel';
import DependentsBlockDialog from '../components/DependentsBlockDialog';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const LS_VIEW_KEY = 'thevault.customers.view';

function loadView() {
  try {
    const v = localStorage.getItem(LS_VIEW_KEY);
    return v === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
}

function saveView(v) {
  try { localStorage.setItem(LS_VIEW_KEY, v); } catch {}
}

// Slugify a customer name to match the logo blob naming convention used by the
// backend (mirrors `slugifyCompany` in api-server/routes/Customers.js).
function sanitizeBaseName(name) {
  const raw = String(name || '').trim();
  if (!raw) return 'upload';
  const dashified = raw.replace(/\s+/g, '-');
  const safe = dashified.replace(/[^a-zA-Z0-9._-]/g, '');
  return safe.replace(/[\\/]+/g, '').replace(/^\.+/, '').slice(0, 120) || 'upload';
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 1rem;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`;

const Header = styled.div`
  background: var(--green-a60);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: var(--white);
  border-radius: 12px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 8px 24px var(--shadow-a08);
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: var(--font-heading, sans-serif);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex: 1;
  min-width: 0;
`;

const ViewToggle = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid var(--white-a25);
  border-radius: 10px;
  background: var(--white-a15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  cursor: pointer;
  font-family: inherit;
  flex: 0 0 auto;

  &:hover { background: var(--white-a20); }
  &:focus-visible { outline: 2px solid var(--white); outline-offset: 2px; }
`;

const SearchBox = styled.label`
  width: 280px;
  height: 36px;
  background: var(--white-a15);
  border: 1px solid var(--white-a25);
  border-radius: 10px;
  color: var(--white);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  font-size: 0.88rem;
  flex: 0 1 280px;
  min-width: 200px;

  &:focus-within { background: var(--white-a20); }
`;

const SearchInput = styled.input`
  background: transparent;
  border: 0;
  outline: 0;
  color: var(--white);
  width: 100%;
  font: inherit;

  &::placeholder { color: var(--white-a55); }
`;

const AddBtn = styled.button`
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  background: var(--white);
  color: var(--green-800);
  font-weight: 700;
  font-size: 0.88rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: inherit;
  flex: 0 0 auto;
  box-shadow: 0 1px 0 var(--shadow-a05);

  &:hover { background: var(--cream-100); }
  &:focus-visible { outline: 2px solid var(--white); outline-offset: 2px; }
`;

// ─── Body layouts ────────────────────────────────────────────────────────────

const ListBody = styled.div`
  display: grid;
  grid-template-columns: ${({ $hasPanel }) => ($hasPanel ? '1fr 460px' : '1fr')};
  gap: 16px;
  margin-top: 14px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ListWrap = styled.div`
  background: var(--white-a55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--white-a66);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 8px 24px var(--shadow-a08);
  overflow: hidden;
  min-width: 0;
`;

const ListHead = styled.div`
  display: grid;
  grid-template-columns: 56px 1fr 200px 32px;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  color: var(--ink-a60);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.10em;
  font-weight: 700;

  @media (max-width: 640px) {
    grid-template-columns: 48px 1fr auto;
    & > :nth-child(4) { display: none; }
  }
`;

const PanelWrap = styled.div`
  position: sticky;
  top: 16px;
  align-self: start;
  min-width: 0;

  @media (max-width: 1024px) {
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: var(--shadow-a15);
    padding: 16px;
    overflow-y: auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-top: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--shadow-a35);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalWrap = styled.div`
  width: min(680px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
`;

// ─── Empty states ────────────────────────────────────────────────────────────

const EmptyWrap = styled.div`
  margin-top: 14px;
  background: var(--white-a55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--white-a66);
  border-radius: 16px;
  padding: 60px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 320px;
  box-shadow: 0 8px 24px var(--shadow-a08);
`;

const EmptyIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${({ $accent }) => ($accent === 'orange' ? 'var(--orange-a12)' : 'var(--green-a08)')};
  color: ${({ $accent }) => ($accent === 'orange' ? 'var(--orange-500)' : 'var(--green-800)')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyTitle = styled.div`
  font-family: var(--font-heading, sans-serif);
  font-weight: 700;
  font-size: 1.1rem;
`;

const EmptyBody = styled.div`
  color: var(--ink-a60);
  max-width: 360px;
  font-size: 0.9rem;
`;

const EmptyAction = styled.button`
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  background: var(--green-800);
  color: var(--white);
  font-weight: 700;
  font-size: 0.88rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--green-900); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const SecondaryAction = styled(EmptyAction)`
  background: var(--white);
  color: var(--black);
  border: 1px solid var(--shadow-a08);

  &:hover { background: var(--grey-100); }
`;

// ─── Component ───────────────────────────────────────────────────────────────

const NEW_KEY = '__new__';

export default function CustomersPage() {
  const [rows, setRows] = useState([]);
  const [logos, setLogos] = useState([]);
  const [view, setView] = useState(loadView());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState(null); // null | NEW_KEY | customer name
  const [blockDialog, setBlockDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  // Suppresses the auto-close useEffect during a rename, where there is a
  // transient window between selectedKey changing to the new name and the
  // refetched rows arriving (during which selectedGroup would be null).
  const renameInProgressRef = useRef(false);

  const collator = useMemo(
    () => new Intl.Collator(undefined, { sensitivity: 'base' }),
    []
  );

  const fetchRows = async () => {
    try {
      const res = await authAxios.get('/api/customers');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Fetch customers failed', e);
    }
  };

  const fetchLogos = async () => {
    try {
      const res = await authAxios.get('/getImages/CustomerSpecs/CustomerLogos');
      const data = res?.data;
      const normalized = Array.isArray(data)
        ? data
        : Array.isArray(data?.files)
          ? data.files
          : Array.isArray(data?.images)
            ? data.images
            : [];
      setLogos(normalized);
    } catch (e) {
      console.error('Fetch logos failed', e);
    }
  };

  const refetch = async () => {
    await Promise.all([fetchRows(), fetchLogos()]);
  };

  useEffect(() => {
    fetchRows();
    fetchLogos();
  }, []);

  // Logo indexes — first by slugified base name, then by exact blobName.
  const logoIndex = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(logos) ? logos : [];
    list.forEach((it) => {
      const full = String(it?.blobName || '');
      const fn = full.replace(/^CustomerSpecs\/CustomerLogos\//, '');
      const withoutExt = fn.replace(/\.[^/.]+$/, '');
      const baseRaw = (withoutExt.split('-')[0] || '').trim();
      const key = sanitizeBaseName(baseRaw).toLowerCase();
      if (!key) return;
      const prev = map.get(key);
      const prevTs = prev?.lastModified ? new Date(prev.lastModified).getTime() : 0;
      const ts = it.lastModified ? new Date(it.lastModified).getTime() : 0;
      if (!prev || ts >= prevTs) map.set(key, it);
    });
    return map;
  }, [logos]);

  const logoByBlobName = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(logos) ? logos : [];
    list.forEach((it) => {
      if (it?.blobName) map.set(String(it.blobName), it);
    });
    return map;
  }, [logos]);

  // Group rows by customer name — destinations into a Set, ids into an array,
  // raw rows kept for the detail panel (so it can use UniqueID per row).
  const grouped = useMemo(() => {
    const groups = new Map();
    rows.forEach((r) => {
      const key = String(r.Customer || '').trim();
      if (!key) return;
      if (!groups.has(key)) {
        groups.set(key, {
          customer: key,
          destinations: new Set(),
          ids: [],
          rows: [],
          logo: null,
        });
      }
      const g = groups.get(key);
      if (r.Destination) g.destinations.add(String(r.Destination).trim());
      g.ids.push(r.UniqueID);
      g.rows.push(r);
    });
    const out = Array.from(groups.values()).map((g) => {
      const base = sanitizeBaseName(g.customer).split('-')[0];
      let it = logoIndex.get(String(base).toLowerCase());
      if (!it) {
        const row = rows.find(
          (r) => String(r.Customer || '').trim() === g.customer
        );
        const blobName = row?.LogoBlobName ? String(row.LogoBlobName) : '';
        if (blobName) it = logoByBlobName.get(blobName) || null;
      }
      return {
        ...g,
        logo: it
          ? {
              url: it.url,
              filename: String(it.blobName || '').replace(
                /^CustomerSpecs\/CustomerLogos\//,
                ''
              ),
            }
          : null,
      };
    });
    return out.sort((a, b) => collator.compare(a.customer, b.customer));
  }, [rows, logoIndex, logoByBlobName, collator]);

  const filtered = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) return grouped;
    return grouped.filter((g) => {
      if (g.customer.toLowerCase().includes(q)) return true;
      for (const d of g.destinations) {
        if (String(d).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [grouped, searchQuery]);

  const isCreating = selectedKey === NEW_KEY;
  const selectedGroup = useMemo(() => {
    if (!selectedKey || isCreating) return null;
    return grouped.find((g) => g.customer === selectedKey) || null;
  }, [grouped, selectedKey, isCreating]);

  // If the selected customer disappears (e.g. after delete), close the panel.
  // Skipped during an in-flight rename to avoid the rename's transient window
  // where selectedKey points at the new name but rows still have the old.
  useEffect(() => {
    if (renameInProgressRef.current) return;
    if (!selectedKey || isCreating) return;
    if (rows.length === 0) return;
    if (selectedGroup) return;
    setSelectedKey(null);
  }, [selectedKey, isCreating, selectedGroup, rows.length]);

  const handleSetView = (v) => {
    setView(v);
    saveView(v);
  };

  const handleSelect = (g) => setSelectedKey(g.customer);
  const handleClose = () => setSelectedKey(null);
  const handleNew = () => setSelectedKey(NEW_KEY);

  const handleRenamed = async (newName) => {
    renameInProgressRef.current = true;
    setSelectedKey(newName);
    try {
      await refetch();
    } finally {
      renameInProgressRef.current = false;
    }
  };

  const aggregateDeps = (results) => {
    let specsCount = 0;
    let tagsCount = 0;
    const specsSample = [];
    const tagsSample = [];
    for (const r of results) {
      if (!r) continue;
      specsCount += r.specs?.count || 0;
      tagsCount += r.caseTags?.count || 0;
      if (Array.isArray(r.specs?.sample)) specsSample.push(...r.specs.sample);
      if (Array.isArray(r.caseTags?.sample)) tagsSample.push(...r.caseTags.sample);
    }
    return {
      specs: {
        count: specsCount,
        sample: specsSample.slice(0, 5),
        hasMore: specsCount > 5,
      },
      caseTags: {
        count: tagsCount,
        sample: tagsSample.slice(0, 5),
        hasMore: tagsCount > 5,
      },
    };
  };

  const handleRequestDeleteDestination = async (rowId, destinationName) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: deps } = await authAxios.get(`/api/customers/${rowId}/dependents`);
      if ((deps?.specs?.count || 0) + (deps?.caseTags?.count || 0) > 0) {
        setBlockDialog({
          customerName: selectedGroup?.customer || '',
          scope: 'destination',
          destinationName,
          dependents: deps,
        });
        return;
      }
      const ok = window.confirm(`Delete destination "${destinationName}"?`);
      if (!ok) return;
      await authAxios.delete(`/api/customers/${rowId}`);
      await refetch();
    } catch (e) {
      console.error('Destination delete flow failed', e);
      alert('Could not delete destination.');
    } finally {
      setBusy(false);
    }
  };

  const handleRequestDeleteCustomer = async (rowIds, customerName) => {
    if (busy) return;
    if (!Array.isArray(rowIds) || rowIds.length === 0) return;
    setBusy(true);
    try {
      const results = await Promise.all(
        rowIds.map((id) =>
          authAxios
            .get(`/api/customers/${id}/dependents`)
            .then((r) => r.data)
            .catch(() => null)
        )
      );
      const deps = aggregateDeps(results);
      if (deps.specs.count + deps.caseTags.count > 0) {
        setBlockDialog({
          customerName,
          scope: 'customer',
          dependents: deps,
        });
        return;
      }
      const ok = window.confirm(
        `Delete customer "${customerName}" and all ${rowIds.length} of its destinations? This cannot be undone.`
      );
      if (!ok) return;
      for (const id of rowIds) {
        // eslint-disable-next-line no-await-in-loop
        await authAxios.delete(`/api/customers/${id}`);
      }
      setSelectedKey(null);
      await refetch();
    } catch (e) {
      console.error('Customer delete flow failed', e);
      alert('Could not delete customer.');
    } finally {
      setBusy(false);
    }
  };

  // Esc closes the detail surface (but not when a block dialog is open;
  // that has its own Esc handler).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (blockDialog) return;
      if (selectedKey) setSelectedKey(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedKey, blockDialog]);

  const isOpen = selectedKey != null;
  const hasNoCustomers = rows.length === 0;
  const hasNoMatches = !hasNoCustomers && filtered.length === 0;

  const detailPanel = (
    <CustomerDetailPanel
      mode={isCreating ? 'create' : 'view'}
      group={isCreating ? null : selectedGroup}
      surface={view === 'grid' ? 'modal' : 'panel'}
      onClose={handleClose}
      refetch={refetch}
      onRenamed={handleRenamed}
      onRequestDeleteDestination={handleRequestDeleteDestination}
      onRequestDeleteCustomer={handleRequestDeleteCustomer}
    />
  );

  return (
    <Container>
      <Header>
        <PageTitle>Customers</PageTitle>

        <ViewToggle
          type="button"
          onClick={() => handleSetView(view === 'list' ? 'grid' : 'list')}
          aria-label={view === 'list' ? 'Switch to card view' : 'Switch to list view'}
          title={view === 'list' ? 'Switch to card view' : 'Switch to list view'}
        >
          {view === 'list'
            ? <LayoutGrid size={18} aria-hidden="true" />
            : <LayoutList size={18} aria-hidden="true" />}
        </ViewToggle>

        <SearchBox>
          <Search size={16} aria-hidden="true" />
          <SearchInput
            type="search"
            placeholder="Search by customer or destination…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search customers"
          />
        </SearchBox>

        <AddBtn type="button" onClick={handleNew} aria-label="Add customer">
          <Plus size={16} aria-hidden="true" />
          Add Customer
        </AddBtn>
      </Header>

      {hasNoCustomers ? (
        <EmptyWrap>
          <EmptyIcon><Building2 size={32} aria-hidden="true" /></EmptyIcon>
          <EmptyTitle>No customers yet</EmptyTitle>
          <EmptyBody>
            Add your first customer to begin tracking destinations, logos, and the
            specifications tied to each one.
          </EmptyBody>
          <EmptyAction type="button" onClick={handleNew}>
            <Plus size={16} aria-hidden="true" />
            Add Customer
          </EmptyAction>
        </EmptyWrap>
      ) : hasNoMatches ? (
        <EmptyWrap>
          <EmptyIcon $accent="orange"><SearchX size={32} aria-hidden="true" /></EmptyIcon>
          <EmptyTitle>No matches for &ldquo;{searchQuery}&rdquo;</EmptyTitle>
          <EmptyBody>
            Try a different customer name or destination, or clear the search to see
            all customers.
          </EmptyBody>
          <SecondaryAction type="button" onClick={() => setSearchQuery('')}>
            Clear search
          </SecondaryAction>
        </EmptyWrap>
      ) : view === 'list' ? (
        <ListBody $hasPanel={isOpen}>
          <ListWrap>
            <ListHead>
              <div />
              <div>Customer</div>
              <div style={{ textAlign: 'right' }}>Destinations</div>
              <div />
            </ListHead>
            {filtered.map((g) => (
              <CustomerListRow
                key={g.customer}
                group={g}
                isSelected={!isCreating && g.customer === selectedKey}
                onSelect={() => handleSelect(g)}
              />
            ))}
          </ListWrap>
          {isOpen && <PanelWrap>{detailPanel}</PanelWrap>}
        </ListBody>
      ) : (
        <>
          <Grid>
            {filtered.map((g) => (
              <CustomerCard
                key={g.customer}
                group={g}
                isSelected={!isCreating && g.customer === selectedKey}
                onSelect={() => handleSelect(g)}
              />
            ))}
          </Grid>
          {isOpen && (
            <ModalBackdrop onClick={handleClose} role="presentation">
              <ModalWrap onClick={(e) => e.stopPropagation()}>
                {detailPanel}
              </ModalWrap>
            </ModalBackdrop>
          )}
        </>
      )}

      <DependentsBlockDialog
        open={Boolean(blockDialog)}
        customerName={blockDialog?.customerName}
        scope={blockDialog?.scope}
        destinationName={blockDialog?.destinationName}
        dependents={blockDialog?.dependents}
        onClose={() => setBlockDialog(null)}
      />
    </Container>
  );
}
